<template>
  <div class="dynamic-field-set">
    <b-row
      :key="rowIndex"
      v-for="(row, rowIndex) in fieldsSetRows"
    >
      <b-col
        v-if="field"
        :md="field.bColMdSize ? field.bColMdSize : defaultBColMdSize"
        :key="index"
        :value="field"
        class="column"
        :class="[field.bColClass ? field.bColClass : (!!index ? 'pl-50' : null)]"
        v-for="(field, index) in row"
      >
        <label :for="field.key + '-' + rowIndex">{{field.label}}</label>
        <template v-if="field.type && field.type === 'select'">
          <b-form-select
            :class="field.classes ? field.classes : ''"
            :disabled="field.disabled && field.disabled === true"
            :id="field.key + '-' + rowIndex"
            label="title"
            :value="getValue(field.key, rowIndex)"
            @change="onChanged($event, field, rowIndex, index)"
          >
            <b-form-select-option v-if="field.placeholder" :value="null" disabled>
              {{ field.placeholder }}
            </b-form-select-option>
            <b-form-select-option
              :key="index"
              :value="item.id"
              v-for="(item, index) in getOptionsFromGetter(field)"
            >
              {{ getSelectOptionLabel(item, field) }}
            </b-form-select-option>
          </b-form-select>
        </template>

        <template v-if="field.type && field.type === 'positiveNumber'">
          <b-form-input
            type="number"
            :disabled="field.disabled && field.disabled === true"
            :class="field.classes ? field.classes : ''"
            :id="field.key + '-' + rowIndex"
            :placeholder="field.placeholder ? field.placeholder : ''"
            :value="getValue(field.key, rowIndex)"
            :formatter="positiveNumberFormatter"
            @input="onChanged($event, field, rowIndex, index)"
          ></b-form-input>
        </template>

        <template v-if="field.type && field.type === 'file'">
          <template v-if="getValue(field.key, rowIndex) && getValue(field.key, rowIndex).src">
            <div
              v-if="field.showDownloadButton"
              title="Скачать"
              class="d-flex align-items-center text-secondary cursor-pointer"
              v-mouseover-class="{overClass: 'text-primary', outClass: 'text-secondary'}"
              @click.stop.prevent="downloadFile(rowIndex)"
            >
              <div class="font-italic font-weight-bold">{{ getValue(field.key, rowIndex).name }}</div>
              <feather-icon
                class="ml-1"
                icon="DownloadIcon"
                size="24"
              ></feather-icon>
            </div>
          </template>
          <template v-else>
            <b-form-file
              :id="field.key + '-' + rowIndex"
              browse-text="Выберите файл"
              placeholder="Файл не выбран"
              @change="onChanged($event, field, rowIndex, index)"
            ></b-form-file>
          </template>
        </template>

        <template v-if="field.type && field.type === 'image'">
          <b-form-file
            v-if="!formData[fieldsSetKey] || !formData[fieldsSetKey][rowIndex] || !formData[fieldsSetKey][rowIndex][field.key] || !formData[fieldsSetKey][rowIndex][field.key].src"
            class="w-100"
            :ref="field.key"
            :id="field.key + '-' + rowIndex"
            @change="handleFileUpload($event, field, rowIndex, index)"
            :accept="accept"
            :browse-text="browseText"
            :placeholder="placeholder"
          />
          <viewer
            v-else
            class="w-100 ml-1"
            :images="[formData[fieldsSetKey][rowIndex][field.key].src]"
          >
            <div class="d-flex align-items-center mr-2 justify-content-center mt-1">
              <b-img
                :src="formData[fieldsSetKey][rowIndex][field.key].src"
                v-bind="imageProps"
                class="image"
                :class="field.icon ? 'icon' : 'w-50'"
                thumbnail
                title="Просмотр изображения"
              />
              <div
                title="Изменить изображение"
                @click="onChanged($event, field, rowIndex, index)"
              >
                <feather-icon
                  class="mx-50 cursor-pointer"
                  icon="XSquareIcon"
                  size="24"
                />
              </div>
            </div>
          </viewer>
        </template>
        <template v-if="field.type && field.type === 'b-media'">
          <b-media
            v-if="getValue(field.key, rowIndex)"
            no-body
          >
            <b-media-aside class="mr-2">
              <b-avatar
                size="36"
                variant="light-success"
              >
                <feather-icon
                  size="20"
                  icon="DollarSignIcon"
                ></feather-icon>
              </b-avatar>
            </b-media-aside>
            <b-media-body class="my-auto">
              <h4 class="font-weight-bolder mb-0">{{ getValue(field.key, rowIndex) }}</h4>
              <b-card-text class="font-small-3 mb-0">{{ field.text }}</b-card-text>
            </b-media-body>
          </b-media>
        </template>
      </b-col>
      <b-col
        v-if="showRemoveButton"
        md="1"
        class="column action-button pl-50"
      >
        <div class="minus-button button-wrapper">
          <b-icon-x-circle-fill
            :variant="removeButtonVariant"
            class="cursor-pointer mt-1"
            :title="removeFieldSetButtonTitle"
            @click="removeFieldSet(rowIndex)"
          ></b-icon-x-circle-fill>
        </div>
      </b-col>
    </b-row>
    <div class="control-wrapper">
      <div class="plus-button button-wrapper d-flex align-items-center">
        <b-icon-plus-circle-fill
          v-if="showPlusButton"
          variant="success"
          class="cursor-pointer mt-1"
          :title="addFieldSetButtonTitle"
          @click="addFieldSet"
        ></b-icon-plus-circle-fill>
        <div
          v-if="showAddFieldSetButtonLabel"
          class="ml-50"
        >{{ addFieldSetButtonLabel ? addFieldSetButtonLabel : addFieldSetButtonTitle }}</div>
      </div>
    </div>
  </div>
</template>

<script>

import {
  BFormInput, BFormSelect, BCol, BRow, BIconPlusCircleFill, BIconTrashFill, BIconXCircleFill, BFormSelectOption,
  BFormFile, BImg, BMedia, BMediaAside, BMediaBody, BCardText, BAvatar,
} from 'bootstrap-vue'
import { downloadFileByUrl } from '@/composables/fileHelper'
import { showFailedOperationToast } from '@/composables/toast'

export default {
  name: 'DynamicFieldSet',
  components: {
    BFormInput, BFormSelect, BCol, BRow, BIconPlusCircleFill, BIconTrashFill, BIconXCircleFill, BFormSelectOption,
    BFormFile, BImg, BAvatar, BCardText, BMediaBody, BMediaAside, BMedia,
  },
  props: {
    fieldsSetKey: {
      type: String,
      required: true
    },
    fieldsSet: {
      type: Array,
      required: true
    },
    formData: {
      type: Object,
      default: () => {}
    },
    showPlusButton: {
      type: Boolean,
      default: () => true
    },
    showRemoveButton: {
      type: Boolean,
      default: () => true
    },
    showAddFieldSetButtonLabel: {
      type: Boolean,
      default: () => false
    },
    addFieldSetButtonLabel: {
      type: [String, null],
      default: () => null
    },
    canRemoveAllFieldSets: {
      type: Boolean,
      default: () => false
    },
  },
  emits: ['updateFormData', 'remove-field-set'],
  watch: {
    formData: {
      handler(value) {
        if (value) {
          this.initFieldSet()
        }
      },
    },
    fieldsSet: {
      handler(value) {
        if (value) {
          this.initFieldSet()
        }
      }
    }
  },
  data() {
    return {
      addFieldSetButtonTitle: 'Добавить набор полей',
      removeFieldSetButtonTitle: 'Удалить набор полей',
      fieldsSetRows: [],
      defaultBColMdSize: 4,
      imageProps: {width: 250},
    }
  },
  computed: {
    accept() {
      return 'image/*'
    },
    browseText() {
      return  'Выбрать'
    },
    placeholder() {
      return 'Изображение'
    },
    removeButtonVariant() {
      return this.fieldsSetRows.length === 1 && !this.canRemoveAllFieldSets ? 'secondary' : 'danger'
    }
  },
  created() {
    this.initFieldSet()
  },
  methods: {
    initFieldSet() {
      this.fieldsSetRows = []
      if (this.fieldsSet && this.formData[this.fieldsSetKey]) {
        const length = this.formData[this.fieldsSetKey].length
        if (length > 0) {
          for (let index = 0; index < length; index++) {
            this.fieldsSetRows.push(this.fieldsSet)
          }
        } else {
          this.fieldsSetRows.push(this.fieldsSet)
        }
      }
      this.$forceUpdate()
    },
    addFieldSet() {
      this.fieldsSetRows.push(this.fieldsSet)
    },
    removeFieldSet(rowIndex) {
      if ((this.canRemoveAllFieldSets && this.fieldsSetRows.length) || this.fieldsSetRows.length > 1) {
        if (this.formData[this.fieldsSetKey] && this.formData[this.fieldsSetKey][rowIndex]) {
          delete this.formData[this.fieldsSetKey][rowIndex]
        }
        if (this.formData[this.fieldsSetKey]) {
          Object.keys(this.formData[this.fieldsSetKey]).map(key => {
            if (rowIndex < key) {
              const temp = {...this.formData[this.fieldsSetKey][key]}
              delete this.formData[this.fieldsSetKey][key]
              this.formData[this.fieldsSetKey][key - 1] = temp
            }
          })
        }
        this.fieldsSetRows.splice(rowIndex, 1)
        this.$forceUpdate()
        this.$emit('remove-field-set')
      }
    },
    getOptionsFromGetter(field) {
      if (field.optionsGetterName) {
        return this.$store.getters[field.optionsGetterName] ? this.$store.getters[field.optionsGetterName] : null
      }
      return null
    },
    getSelectOptionLabel(item, field) {
      let label = 'name'
      if (field && field.selectSetting && field.selectSetting.value) {
        let labels = field.selectSetting.value.split(',')
        if (labels.length > 0) {
          label = ''
          labels.forEach(key => {
            label += `${item[key]} `
          })
          return label
        }
      }
      return item[label]
    },
    onChanged(event, field, rowIndex, fieldIndex) {
      rowIndex = rowIndex.toString()
      fieldIndex = fieldIndex.toString()
      if (!this.formData[this.fieldsSetKey]) {
        this.formData[this.fieldsSetKey] = {}
      }
      if (!this.formData[this.fieldsSetKey][rowIndex]) {
        this.formData[this.fieldsSetKey][rowIndex] = {}
      }
      if (field.type === 'image') {
        delete this.formData[this.fieldsSetKey][rowIndex][field.key]
      } else {
        this.formData[this.fieldsSetKey][rowIndex][field.key] = event
      }
      field['rowIndex'] = rowIndex
      field['fieldIndex'] = fieldIndex
      this.$emit('updateFormData', {'formData': this.formData, 'field': field})
      this.$forceUpdate()
    },
    getValue(fieldKey, rowIndex) {
      if (
        this.formData[this.fieldsSetKey]
        && this.formData[this.fieldsSetKey][rowIndex]
        && this.formData[this.fieldsSetKey][rowIndex][fieldKey]
      ) {
        return this.formData[this.fieldsSetKey][rowIndex][fieldKey]
      }
      return null
    },
    handleFileUpload(event, field, rowIndex, fieldIndex) {
      rowIndex = rowIndex.toString()
      fieldIndex = fieldIndex.toString()
      if (!this.formData[this.fieldsSetKey]) {
        this.formData[this.fieldsSetKey] = {}
      }
      if (!this.formData[this.fieldsSetKey][rowIndex]) {
        this.formData[this.fieldsSetKey][rowIndex] = {}
      }
      if (event.target.files.length) {
        const file = event.target.files[0]
        const reader = new FileReader();
        reader.onload = e => {
          this.formData[this.fieldsSetKey][rowIndex][field.key] = {name: file.name, src: e.target.result, file: file}
          field['rowIndex'] = rowIndex
          field['fieldIndex'] = fieldIndex
          this.$emit('updateFormData', {'formData': this.formData, 'field': field})
          this.$forceUpdate()
        }
        reader.readAsDataURL(file)
      } else {
        this.formData[this.fieldsSetKey][rowIndex][field.key] = null
        this.$emit('updateFormData', {'formData': this.formData, 'field': field})
      }
    },
    positiveNumberFormatter(value) {
      if (value) {
        value = value < 0 ? Math.abs(value).toString() : value
        if (value.charAt(0) === '0' && value.charAt(1) !== '.' && value.length > 1) {
          value = value.slice(1)
        }
      }
      return value
    },
  },
  setup(props) {
    const downloadFile = rowIndex => {
      if (
        props.formData && props.formData.files && props.formData.files.length && props.formData.files[rowIndex]
        && props.formData.files[rowIndex].file
      ) {
        const f = props.formData.files[rowIndex].file
        downloadFileByUrl(f.src, f.name)
      } else {
        showFailedOperationToast('Не удается скачать файл')
      }
    }

    return {
      downloadFile
    }
  }
}
</script>

<style lang='scss'>

.dynamic-field-set {
  .control-wrapper {
    display: flex;
    flex-direction: column;
    .button-wrapper {
      display: flex;
      justify-content: left;
      .b-icon {
        margin-top: 6px !important;
        margin-bottom: 6px;
        width: 20px;
        height: 20px;
      }
      .button-title {
        margin-left: 5px;
      }
    }
  }

  .row {
    margin-bottom: 10px;

    .column {
      padding-right: 0;

      &.action-button {
        padding-right: 0;
        padding-left: 0;
        display: flex;
        align-items: end;
        padding-bottom: 7px;
        .b-icon {
          margin-top: 0 !important;
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .row {
      margin-left: 0;
      margin-right: 0;

      .column {
        width: calc(100% - 0.5rem - 20px);
        padding-left: 0;

        &.action-button {
          width: auto;
        }
      }
    }
  }
}

</style>
